import { useEffect, useState } from "react";
import { api } from "../api";
import { ArrowRight, ShieldCheck, Radar, MapPin, TrendingUp, CheckCircle2, Lock, Search, X } from "lucide-react";
import SEO from "../components/SEO";

const STATUS_COLOR = {
  new: "#065F46",
  under_offer: "#B45309",
  under_dd: "var(--terracotta)",
  closed: "var(--midnight)",
};

export default function Marketplace() {
  const [dossiers, setDossiers] = useState([]);
  const [filterSector, setFilterSector] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [modalDossier, setModalDossier] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [radarForm, setRadarForm] = useState({
    email: "", contact_name: "", buyer_type: "", available_capital_eur_m: "",
    target_sectors: "", target_regions: "", proof_of_funds: false,
  });
  const [radarStatus, setRadarStatus] = useState(null);
  const [radarResult, setRadarResult] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (filterSector) q.set("sector", filterSector);
    if (filterRegion) q.set("region", filterRegion);
    api.get(`/platform/dossiers${q.toString() ? "?" + q.toString() : ""}`)
      .then(r => setDossiers(r.data.dossiers || []));
  }, [filterSector, filterRegion]);

  const searchDossier = async (e) => {
    e.preventDefault();
    const code = searchCode.trim().toUpperCase();
    if (!code) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const r = await api.get(`/platform/dossiers/${encodeURIComponent(code)}`);
      setModalDossier(r.data);
    } catch (err) {
      setSearchError(err?.response?.status === 404 ? `Nessun dossier con codice "${code}".` : "Errore di ricerca. Riprova.");
    } finally {
      setSearchLoading(false);
    }
  };

  const submitRadar = async (e) => {
    e.preventDefault();
    setRadarStatus("loading");
    try {
      const payload = { ...radarForm };
      payload.available_capital_eur_m = payload.available_capital_eur_m === "" ? null : Number(payload.available_capital_eur_m);
      const r = await api.post("/platform/deal-radar/subscribe", payload);
      setRadarResult(r.data);
      setRadarStatus("sent");
    } catch { setRadarStatus("error"); }
  };

  const sectors = [...new Set(dossiers.map(d => d.sector))].filter(Boolean);
  const regions = [...new Set(dossiers.map(d => d.region))].filter(Boolean);

  return (
    <main data-testid="marketplace-page">
      <SEO
        title="Marketplace PMI · Deal Radar Live"
        description="Feed di dossier anonimi verificati di PMI italiane in transizione. Deal Radar: alert email sui nuovi dossier che corrispondono ai tuoi criteri. Prova fondi obbligatoria, anonimato del venditore fino all'NDA digitale."
        path="/marketplace"
      />
      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--gold)" }}>Marketplace a rivelazione progressiva · Deal Radar Live</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 1000 }}>
            Dossier verificati.<br />
            <span style={{ color: "var(--gold)", fontStyle: "italic" }}>Alert sui match.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 800, opacity: 0.85, lineHeight: 1.7 }}>
            Il venditore mostra numeri e settore. Il nome dell'azienda solo dopo NDA digitale.
            Il compratore mostra il capitale con attestazione bancaria — nessun perditempo, nessun turista del deal.
          </p>

          {/* SEARCH BAR: cerca dossier per codice */}
          <form onSubmit={searchDossier} className="mt-10 flex flex-col md:flex-row gap-3" data-testid="dossier-search-form" style={{ maxWidth: 620 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={18} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--gold)", opacity: 0.8 }} />
              <input
                data-testid="dossier-search-input"
                value={searchCode}
                onChange={e => { setSearchCode(e.target.value); setSearchError(null); }}
                placeholder="Cerca un dossier per codice · es. GH-2026-330"
                style={{
                  width: "100%", padding: "14px 16px 14px 42px",
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.2)",
                  color: "var(--bone)", fontFamily: "monospace", fontSize: 14, letterSpacing: 1,
                  outline: "none",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={searchLoading || !searchCode.trim()}
              data-testid="dossier-search-btn"
              className="btn-primary"
              style={{ background: "var(--gold)", color: "var(--midnight)", borderColor: "var(--gold)" }}
            >
              {searchLoading ? "..." : "Apri dossier"} <ArrowRight size={16} />
            </button>
          </form>
          {searchError && (
            <div className="mt-3 text-sm font-mono" style={{ color: "#F87171" }} data-testid="dossier-search-error">
              {searchError}
            </div>
          )}
        </div>
      </section>

      {/* DEAL RADAR — Feed dossier */}
      <section className="py-16 md:py-20" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          <div className="flex items-center gap-3">
            <Radar size={22} style={{ color: "var(--terracotta)" }} />
            <div className="overline" style={{ color: "var(--terracotta)" }}>Deal Radar · Dossier attivi</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-4">
            <div>
              <h2 className="serif-display" style={{ fontSize: "clamp(28px, 3.5vw, 44px)", maxWidth: 700 }}>
                <b>{dossiers.length}</b> dossier verificati sul radar oggi
              </h2>
              <p className="mt-3 text-sm" style={{ opacity: 0.7 }}>
                Aggiornato in tempo reale. Ogni dossier è anonimo (codice GH-2026-XXX) fino al momento dell'NDA digitale.
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="dossier-filters">
              <select
                data-testid="filter-sector"
                value={filterSector}
                onChange={e => setFilterSector(e.target.value)}
                className="input-line"
                style={{ padding: "8px 12px", background: "transparent", minWidth: 160 }}
              >
                <option value="">Tutti i settori</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                data-testid="filter-region"
                value={filterRegion}
                onChange={e => setFilterRegion(e.target.value)}
                className="input-line"
                style={{ padding: "8px 12px", background: "transparent", minWidth: 160 }}
              >
                <option value="">Tutte le regioni</option>
                {regions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {dossiers.map(d => (
              <div key={d.code} className="card-flat p-6 flex flex-col" data-testid={`dossier-${d.code}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>{d.code}</span>
                  <span className="font-mono text-xs px-2 py-1" style={{ background: STATUS_COLOR[d.status], color: "white" }}>
                    {d.status_label}
                  </span>
                </div>
                <h3 className="serif-display mt-4" style={{ fontSize: 22 }}>{d.sector}</h3>
                <div className="mt-1 text-sm flex items-center gap-1" style={{ opacity: 0.65 }}>
                  <MapPin size={12} /> {d.region}
                </div>
                <div className="mt-5 space-y-2 text-sm">
                  <Row k="Ricavi" v={d.revenue_eur_m_range} />
                  <Row k="EBITDA" v={d.ebitda_eur_m_range} />
                  <Row k="Margine" v={d.ebitda_margin_pct_range} />
                  <Row k="Addetti" v={d.employees_range} />
                  <Row k="Prontezza" v={`Fascia ${d.score_readiness_band}`} accent />
                </div>
                <div className="mt-5 pt-4 flex items-center gap-2 text-xs" style={{ borderTop: "1px solid var(--border)", opacity: 0.7 }}>
                  <ShieldCheck size={13} style={{ color: "#065F46" }} /> Verificato · <Lock size={11} /> Nome sotto NDA
                </div>
                <p className="mt-3 text-xs font-mono" style={{ opacity: 0.55 }}>{d.highlight}</p>
              </div>
            ))}
          </div>
          {dossiers.length === 0 && (
            <div className="text-center py-16 text-sm" style={{ opacity: 0.6 }}>Nessun dossier corrisponde ai filtri attuali.</div>
          )}
        </div>
      </section>

      {/* DEAL RADAR — Iscrizione compratori */}
      <section className="py-16 md:py-20" style={{ background: "var(--midnight)", color: "var(--bone)" }}>
        <div className="container-x max-w-3xl">
          <div className="overline" style={{ color: "var(--gold)" }}>Deal Radar · Iscriviti agli alert</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(30px, 4vw, 52px)" }}>
            Ricevi in email i dossier<br />
            <span style={{ color: "var(--gold)", fontStyle: "italic" }}>che corrispondono a te.</span>
          </h2>
          <p className="mt-6 text-base" style={{ opacity: 0.85, maxWidth: 640, lineHeight: 1.65 }}>
            Definisci settore, regione e taglia. Ogni volta che un nuovo dossier entra nel radar e combacia con i tuoi criteri, ti arriva un alert. Zero rumore.
          </p>

          {radarStatus === "sent" ? (
            <div className="mt-10 p-8" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)" }} data-testid="radar-success">
              <CheckCircle2 size={32} style={{ color: "var(--gold)" }} />
              <h3 className="serif-display mt-4" style={{ fontSize: 26 }}>Iscrizione attiva</h3>
              <p className="mt-3 text-sm" style={{ opacity: 0.85 }}>
                Il primo alert è già in coda per <b>{radarResult?.email}</b>. Ci sono {radarResult?.first_alert?.dossier_codes?.length || 0} dossier che corrispondono ora: {radarResult?.first_alert?.dossier_codes?.join(", ")}.
              </p>
              <p className="mt-4 text-xs font-mono" style={{ opacity: 0.55 }}>
                ID iscrizione: {radarResult?.subscription_id}
              </p>
            </div>
          ) : (
            <form onSubmit={submitRadar} className="mt-10 p-8" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="radar-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                <RF label="Email *">
                  <input required type="email" data-testid="radar-email" className="input-line-dark" value={radarForm.email} onChange={e => setRadarForm({ ...radarForm, email: e.target.value })} />
                </RF>
                <RF label="Nome">
                  <input data-testid="radar-name" className="input-line-dark" value={radarForm.contact_name} onChange={e => setRadarForm({ ...radarForm, contact_name: e.target.value })} />
                </RF>
                <RF label="Tipologia compratore">
                  <select data-testid="radar-btype" className="input-line-dark" style={{ background: "transparent" }} value={radarForm.buyer_type} onChange={e => setRadarForm({ ...radarForm, buyer_type: e.target.value })}>
                    <option value="" style={{ color: "black" }}>—</option>
                    <option style={{ color: "black" }}>Search fund</option>
                    <option style={{ color: "black" }}>Family office</option>
                    <option style={{ color: "black" }}>Acquirente strategico</option>
                    <option style={{ color: "black" }}>Imprenditore individuale</option>
                    <option style={{ color: "black" }}>Fondo</option>
                  </select>
                </RF>
                <RF label="Capitale disponibile (M€)">
                  <input type="number" step="0.5" data-testid="radar-cap" className="input-line-dark" value={radarForm.available_capital_eur_m} onChange={e => setRadarForm({ ...radarForm, available_capital_eur_m: e.target.value })} />
                </RF>
                <RF label="Settori target (comma-separated)">
                  <input data-testid="radar-sectors" className="input-line-dark" placeholder="es. Meccanica, Alimentare" value={radarForm.target_sectors} onChange={e => setRadarForm({ ...radarForm, target_sectors: e.target.value })} />
                </RF>
                <RF label="Regioni target">
                  <input data-testid="radar-regions" className="input-line-dark" placeholder="es. Lombardia, Veneto" value={radarForm.target_regions} onChange={e => setRadarForm({ ...radarForm, target_regions: e.target.value })} />
                </RF>
              </div>
              <label className="flex items-start gap-3 mt-6 text-sm" style={{ opacity: 0.9 }}>
                <input type="checkbox" data-testid="radar-pof" checked={radarForm.proof_of_funds} onChange={e => setRadarForm({ ...radarForm, proof_of_funds: e.target.checked })} style={{ marginTop: 4 }} />
                <span>Posso fornire attestazione bancaria/prova fondi. (Requisito per accedere ai dettagli dopo NDA.)</span>
              </label>
              <button type="submit" disabled={radarStatus === "loading"} className="btn-primary mt-8" data-testid="radar-submit" style={{ background: "var(--gold)", color: "var(--midnight)", borderColor: "var(--gold)" }}>
                {radarStatus === "loading" ? "..." : "Attiva Deal Radar"} <ArrowRight size={16} />
              </button>
              {radarStatus === "error" && <p className="mt-4 text-sm" style={{ color: "var(--terracotta)" }}>Errore di rete. Riprova.</p>}
            </form>
          )}
        </div>
      </section>

      {/* SIGNUP profilo venditore/compratore verificato (mantenuto) */}
      <section className="py-16" style={{ background: "var(--muted)" }}>
        <div className="container-x max-w-3xl">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Sei venditore? Registra il profilo verificato</div>
          <div className="mt-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
            <p className="text-sm" style={{ opacity: 0.78, maxWidth: 560, lineHeight: 1.65 }}>
              Se sei un imprenditore che considera la successione, o un compratore che vuole accedere ai dossier completi, registra un profilo verificato dedicato.
            </p>
            <a href="/piattaforma" className="btn-primary whitespace-nowrap" data-testid="link-piattaforma">
              Registrati alla piattaforma <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .input-line-dark {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(255,255,255,0.3);
          color: var(--bone);
          padding: 8px 0;
          font-family: inherit;
          font-size: 14px;
        }
        .input-line-dark:focus {
          outline: none;
          border-bottom-color: var(--gold);
        }
      `}</style>

      {modalDossier && <DossierModal dossier={modalDossier} onClose={() => setModalDossier(null)} />}
    </main>
  );
}

function DossierModal({ dossier, onClose }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,32,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}
      onClick={onClose}
      data-testid="dossier-modal"
    >
      <div className="card-flat" style={{ maxWidth: 640, width: "100%", background: "var(--bone)", position: "relative" }} onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="dossier-modal-close"
          style={{ position: "absolute", top: 16, right: 16, background: "transparent", border: "none", cursor: "pointer" }}
          aria-label="Chiudi"
        >
          <X size={20} />
        </button>
        <div style={{ background: "var(--midnight)", color: "var(--bone)", padding: "28px 32px" }}>
          <div className="font-mono text-xs" style={{ color: "var(--gold)", letterSpacing: 2 }}>{dossier.code}</div>
          <h3 className="serif-display mt-3" style={{ fontSize: 30 }}>{dossier.sector}</h3>
          <div className="text-sm mt-2" style={{ opacity: 0.7 }}>
            <MapPin size={12} style={{ display: "inline" }} /> {dossier.region}
          </div>
        </div>
        <div className="p-8">
          <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>{dossier.status_label}</div>
          <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <ModalRow k="Ricavi" v={dossier.revenue_eur_m_range} />
            <ModalRow k="EBITDA" v={dossier.ebitda_eur_m_range} />
            <ModalRow k="Margine EBITDA" v={dossier.ebitda_margin_pct_range} />
            <ModalRow k="Addetti" v={dossier.employees_range} />
            <ModalRow k="Fondazione" v={`~${dossier.years_founded_approx}`} />
            <ModalRow k="Prontezza" v={`Fascia ${dossier.score_readiness_band}`} accent />
          </div>
          <div className="mt-6 pt-5 flex items-start gap-3 text-sm" style={{ borderTop: "1px solid var(--border)" }}>
            <ShieldCheck size={16} style={{ color: "#065F46", flexShrink: 0, marginTop: 2 }} />
            <span style={{ opacity: 0.75, lineHeight: 1.6 }}>
              Dossier verificato. Motivo cessione: <b>{dossier.succession_reason}</b>. Il nome dell'azienda è visibile solo dopo firma dell'NDA digitale.
            </span>
          </div>
          <p className="mt-4 text-xs font-mono" style={{ opacity: 0.6 }}>{dossier.highlight}</p>
          <div className="mt-6 flex gap-3">
            <a href="/piattaforma" className="btn-primary" data-testid="dossier-modal-cta">
              Richiedi accesso completo <ArrowRight size={16} />
            </a>
            <button onClick={onClose} className="btn-ghost">Chiudi</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalRow({ k, v, accent }) {
  return (
    <div>
      <div className="font-mono text-xs" style={{ opacity: 0.55, textTransform: "uppercase", letterSpacing: 1 }}>{k}</div>
      <div className="mt-1 serif-display" style={{ fontSize: 20, color: accent ? "var(--terracotta)" : "inherit" }}>{v}</div>
    </div>
  );
}

function Row({ k, v, accent }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="font-mono text-xs" style={{ opacity: 0.55 }}>{k}</span>
      <span className="font-mono text-sm" style={{ color: accent ? "var(--terracotta)" : "inherit", fontWeight: accent ? 600 : 400 }}>{v}</span>
    </div>
  );
}
function RF({ label, children }) {
  return (
    <div>
      <label className="font-mono text-xs" style={{ opacity: 0.55, textTransform: "uppercase", letterSpacing: 1 }}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
